import React from 'react';
import { BookOpen, TrendingUp, Clock, Award, Users, Play, LogOut } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/admin/AdminLayout';

export default function Dashboard() {
  const navigate = useNavigate();
  
  // Example user data - replace with actual data from your auth context
  const user = {
    first_name: 'John',
    last_name: 'Doe',
    email: 'john@example.com',
    avatar: 'https://ui-avatars.com/api/?name=John+Doe&background=8B5CF6&color=fff&size=128',
    memberSince: 'January 2025'
  };

  // Helper to get full display name
  const getDisplayName = () => {
    if (user.first_name || user.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return user.email;
  };

  const handleSignOut = () => {
    // Replace with your actual sign-out logic
    localStorage.removeItem('user');
    localStorage.removeItem('access_token');
    navigate('/signin');
  };

  // Example data - replace with actual API calls
  const stats = [
    { label: 'Courses Enrolled', value: '8', icon: BookOpen, color: 'from-purple-600 to-blue-600' },
    { label: 'Hours Learned', value: '47', icon: Clock, color: 'from-blue-600 to-cyan-600' },
    { label: 'Certificates', value: '3', icon: Award, color: 'from-cyan-600 to-green-600' },
    { label: 'Current Streak', value: '12 days', icon: TrendingUp, color: 'from-green-600 to-yellow-600' }
  ];

  const currentCourses = [
    {
      id: 1,
      title: 'Advanced Prompting & Multi-Agent Structuring',
      progress: 65,
      thumbnail: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=300&fit=crop',
      nextLesson: 'Building Custom GPT Agents'
    },
    {
      id: 2,
      title: 'AI for Inbox Zero + Communication Mastery',
      progress: 40,
      thumbnail: 'https://images.unsplash.com/photo-1676277791608-ac52e6608b47?w=400&h=300&fit=crop',
      nextLesson: 'Email Automation Workflows'
    },
    {
      id: 3,
      title: 'How to Launch a One-Person Business Using AI',
      progress: 25,
      thumbnail: 'https://images.unsplash.com/photo-1661956602116-aa6865609028?w=400&h=300&fit=crop',
      nextLesson: 'Market Research with AI'
    }
  ];

  const recentAchievements = [
    { title: 'First Course Completed', date: '2 days ago', icon: '🎓' },
    { title: 'Week Streak Achieved', date: '5 days ago', icon: '🔥' },
    { title: 'Community Member', date: '1 week ago', icon: '👥' }
  ];

  return (
    <AdminLayout>
      {/* Dashboard Content */}
      <div className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          
          {/* Header Section */}
          <div className="mb-12">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex items-center space-x-4">
                <img 
                  src={user.avatar} 
                  alt={getDisplayName()}
                  className="w-16 h-16 rounded-full border-2 border-purple-500/50"
                />
                <div>
                  <h1 className="text-3xl font-bold">Welcome back, {getDisplayName()}!</h1>
                  <p className="text-gray-400">Member since {user.memberSince}</p>
                </div>
              </div>
              <button
                onClick={handleSignOut}
                className="flex items-center space-x-2 bg-white/5 border border-white/10 px-6 py-3 rounded-xl hover:bg-white/10 transition-all"
              >
                <LogOut className="w-4 h-4" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="relative group">
                  <div className={`absolute inset-0 bg-gradient-to-r ${stat.color} rounded-2xl blur-xl opacity-20 group-hover:opacity-30 transition-all`}></div>
                  <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/20 hover:border-white/30 transition-all">
                    <div className="flex items-center justify-between mb-4">
                      <div className={`w-12 h-12 bg-gradient-to-r ${stat.color} rounded-xl flex items-center justify-center`}>
                        <Icon className="w-6 h-6" />
                      </div>
                    </div>
                    <div className="text-3xl font-bold mb-1">{stat.value}</div>
                    <div className="text-sm text-gray-400">{stat.label}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content Area */}
            <div className="lg:col-span-2 space-y-8">
              
              {/* Continue Learning Section */}
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">Continue Learning</h2>
                  <Link to="/courses" className="text-purple-400 hover:text-purple-300 text-sm font-semibold transition-colors">
                    Browse All Courses →
                  </Link>
                </div>
                
                <div className="space-y-6">
                  {currentCourses.map((course) => (
                    <div key={course.id} className="relative group">
                      <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl blur-xl opacity-0 group-hover:opacity-20 transition-all"></div>
                      <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/20 hover:border-purple-500/50 transition-all">
                        <div className="flex flex-col md:flex-row gap-6">
                          <div className="relative w-full md:w-48 h-36 rounded-xl overflow-hidden flex-shrink-0">
                            <img 
                              src={course.thumbnail} 
                              alt={course.title}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                                <Play className="w-6 h-6 text-black fill-black ml-1" />
                              </div>
                            </div>
                          </div>
                          <div className="flex-1">
                            <h3 className="text-xl font-bold mb-2">{course.title}</h3>
                            <p className="text-sm text-gray-400 mb-4">
                              Next: {course.nextLesson}
                            </p>
                            <div className="mb-3">
                              <div className="flex items-center justify-between text-sm mb-2">
                                <span className="text-gray-400">Progress</span>
                                <span className="font-semibold">{course.progress}%</span>
                              </div>
                              <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                                <div 
                                  className="bg-gradient-to-r from-purple-600 to-blue-600 h-full rounded-full transition-all duration-300"
                                  style={{ width: `${course.progress}%` }}
                                ></div>
                              </div>
                            </div>
                            <button className="bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-2 rounded-full text-sm font-semibold hover:shadow-lg hover:shadow-purple-500/50 transition-all">
                              Continue Learning
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recommended Courses */}
              <div>
                <h2 className="text-2xl font-bold mb-6">Recommended for You</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {[1, 2].map((item) => (
                    <div key={item} className="relative group">
                      <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-green-600 rounded-2xl blur-xl opacity-0 group-hover:opacity-20 transition-all"></div>
                      <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/20 hover:border-cyan-500/50 transition-all">
                        <div className="w-12 h-12 bg-gradient-to-r from-cyan-600 to-green-600 rounded-xl flex items-center justify-center mb-4">
                          <BookOpen className="w-6 h-6" />
                        </div>
                        <h3 className="text-lg font-bold mb-2">AI-Enhanced Decision Making</h3>
                        <p className="text-sm text-gray-400 mb-4">
                          Master data-driven decisions with AI assistance
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-400">6 hours • 24 lessons</span>
                          <button className="text-cyan-400 hover:text-cyan-300 font-semibold text-sm transition-colors">
                            View →
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              
              {/* Recent Achievements */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-600 to-orange-600 rounded-2xl blur-xl opacity-20"></div>
                <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/20">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <Award className="w-5 h-5 mr-2 text-yellow-400" />
                    Recent Achievements
                  </h3>
                  <div className="space-y-4">
                    {recentAchievements.map((achievement, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="text-2xl">{achievement.icon}</div>
                        <div>
                          <div className="font-semibold text-sm">{achievement.title}</div>
                          <div className="text-xs text-gray-400">{achievement.date}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Community Activity */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl blur-xl opacity-20"></div>
                <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/20">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <Users className="w-5 h-5 mr-2 text-purple-400" />
                    Community
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">Active Users</span>
                      <span className="font-semibold">12,458</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">Discussion Posts</span>
                      <span className="font-semibold">1,247</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">Your Rank</span>
                      <span className="font-semibold">#156</span>
                    </div>
                  </div>
                  <button className="w-full mt-4 bg-white/10 border border-white/10 px-4 py-3 rounded-xl hover:bg-white/20 transition-all text-sm font-semibold">
                    Join Discussion
                  </button>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl blur-xl opacity-20"></div>
                <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/20">
                  <h3 className="text-xl font-bold mb-4">Quick Actions</h3>
                  <div className="space-y-3">
                    <button className="w-full bg-white/5 border border-white/10 px-4 py-3 rounded-xl hover:bg-white/10 transition-all text-sm font-semibold text-left">
                      📚 Browse All Courses
                    </button>
                    <button className="w-full bg-white/5 border border-white/10 px-4 py-3 rounded-xl hover:bg-white/10 transition-all text-sm font-semibold text-left">
                      📜 View Certificates
                    </button>
                    <button className="w-full bg-white/5 border border-white/10 px-4 py-3 rounded-xl hover:bg-white/10 transition-all text-sm font-semibold text-left">
                      ⚙️ Account Settings
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}